import type { TFunction } from 'i18next';
import type { UseFormReturn } from 'react-hook-form';
import type { ApiCountry } from '@ui/country-dropdown.tsx';
import type { WaitingListWaitingListWriteModelRole } from '@r/api/generated/client.schemas.ts';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { CountryDropdown } from '@ui/country-dropdown.tsx';
import type { WaitingListProposeFormValues } from '@admin/components/company/waiting-list/waiting-list-propose-form-schema.ts';

export function WaitingListProposeCompanyFields({
  form,
  countries,
  isCountriesLoading,
  typologyConversionMode,
  typologies,
  typologiesLoading,
  t
}: {
  form: UseFormReturn<WaitingListProposeFormValues>;
  countries: ApiCountry[];
  isCountriesLoading: boolean;
  typologyConversionMode: boolean;
  typologies: Array<{ id: number; name: string }>;
  typologiesLoading: boolean;
  t: TFunction;
}) {
  const countryAlpha3 = form.watch('countryAlpha3');

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="companyName"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('label.company.name')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="companyUniqueCode"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('company.create.unique.code.label')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="tva"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('label.company.tva')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="siren"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('label.company.siren')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="addressLine1"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('label.company.address.line1')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="addressLine2"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>{t('label.company.address.line.2')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-start">
        <FormField
          control={form.control}
          name="zipcode"
          render={({ field, fieldState }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>{t('label.company.zipcode')}</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="city"
          render={({ field, fieldState }) => (
            <FormItem className="md:col-span-8">
              <FormLabel>{t('label.company.city')}</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="countryAlpha3"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>{t('label.company.country')}</FormLabel>
              <CountryDropdown
                placeholder={t('label.company.country')}
                defaultValue={countryAlpha3}
                loading={isCountriesLoading}
                options={countries}
                slim
                onChange={(country: ApiCountry) => {
                  field.onChange(country.alpha3);
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {typologyConversionMode ? (
        <FormField
          control={form.control}
          name="typology"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('company.create.typology.name.label')}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || undefined}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('company.create.typology.name.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {typologiesLoading ? (
                    <SelectItem value="__loading" disabled>
                      {t('company.create.typology.name.loading')}
                    </SelectItem>
                  ) : (
                    typologies.map((typology) => (
                      <SelectItem key={typology.id} value={`/admin/typologies/${typology.id}`}>
                        {typology.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <FormField
          control={form.control}
          name="pointCoeff"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('label.company.point.coefficient')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={
                    field.value === undefined || field.value === null ? '' : String(field.value)
                  }
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (v === '') {
                      field.onChange(undefined);
                      return;
                    }
                    const n = Number(v.replace(',', '.'));
                    field.onChange(Number.isNaN(n) ? undefined : n);
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

export function WaitingListProposeManagerFields({
  form,
  roleOptions,
  t
}: {
  form: UseFormReturn<WaitingListProposeFormValues>;
  roleOptions: { value: WaitingListWaitingListWriteModelRole; label: string }[];
  t: TFunction;
}) {
  return (
    <div className="flex flex-col gap-4 w-full">
      <FormField
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <FormItem className="w-full">
            <FormLabel>{t('label.user.email')}</FormLabel>
            <FormControl>
              <Input type="email" {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="firstname"
        render={({ field, fieldState }) => (
          <FormItem className="w-full">
            <FormLabel>{t('admin.waiting.list.edit.label.user.firstname')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="lastname"
        render={({ field, fieldState }) => (
          <FormItem className="w-full">
            <FormLabel>{t('admin.waiting.list.edit.label.user.lastname')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} error={!!fieldState.error} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('label.user.role')}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={
                  field.value === null || field.value === undefined || field.value === ''
                    ? undefined
                    : field.value
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('label.user.role')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {roleOptions.map((opt) => (
                    <SelectItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="comment"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('label.comment')}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={4}
                className="resize-y min-h-[100px]"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
